import React, { useState, useEffect, useRef } from 'react';
import { Plus, Search, Edit3, Layers, MessageSquare, FileText, Calendar, Save, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import api from '../services/api';
import IconButton from '../components/IconButton';

const Regras = () => {
  const { user } = useAuth();
  const [regras, setRegras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRegra, setEditingRegra] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    desc_regra: '',
    idade_inicial_regra: '',
    idade_final_regra: '',
    taxa: '',
    ativo_taxa: '',
    dt_desativa_regra: '',
    obs_taxa: '',
    reajuste_regra: ''
  });

  const [obsModalItem, setObsModalItem] = useState(null);
  const [reajusteModalItem, setReajusteModalItem] = useState(null);
  
  // Refs para date picker
  const dtDesativaRef = useRef(null);

  useEffect(() => { loadRegras(); }, []);
  useEffect(() => {
    document.title = 'Regras - EstOX';
  }, []);

  const loadRegras = async () => {
    try {
      const response = await api.get('/regras');
      setRegras(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar regras:', error);
      toast.error('Erro ao carregar regras');
    } finally {
      setLoading(false);
    }
  };

  const filteredRegras = regras.filter(r => (
    String(r.desc_regra ?? '').toLowerCase().includes(String(searchTerm).toLowerCase()) ||
    String(r.taxa ?? '').toLowerCase().includes(String(searchTerm).toLowerCase())
  ));

  const formatDateInput = (raw) => {
    const digits = String(raw || '').replace(/\D/g, '').slice(0,8);
    const dd = digits.slice(0,2);
    const mm = digits.slice(2,4);
    const yyyy = digits.slice(4,8);
    let out = '';
    if (dd) out += dd;
    if (mm) out += '/' + mm;
    if (yyyy) out += '/' + yyyy;
    return out;
  };

  const formatDateToISO = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('/');
    if (parts.length !== 3) return null;
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  };
  
  const formatDateDisplay = (isoString) => {
    if (!isoString) return '-';
    // Ajuste de fuso horário simples ou split direto
    if (isoString.includes('T')) {
        return new Date(isoString).toLocaleDateString('pt-BR');
    }
    const [yyyy, mm, dd] = isoString.split('-');
    return `${dd}/${mm}/${yyyy}`;
  };
  
  const isoToDisplay = (iso) => {
    if (!iso) return '';
    const m = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(String(iso));
    if (!m) return '';
    const yyyy = m[1], mm = m[2], dd = m[3];
    return `${dd}/${mm}/${yyyy}`;
  };

  const openModal = (regra = null) => {
    setEditingRegra(regra);
    setFormData({
      desc_regra: regra?.desc_regra || '',
      idade_inicial_regra: regra?.idade_inicial_regra || '',
      idade_final_regra: regra?.idade_final_regra || '',
      taxa: regra?.taxa || '',
      ativo_taxa: regra?.ativo_taxa ?? '1',
      dt_desativa_regra: regra?.dt_desativa_regra ? formatDateDisplay(regra.dt_desativa_regra) : '',
      obs_taxa: regra?.obs_taxa || '',
      reajuste_regra: regra?.reajuste_regra || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingRegra(null);
    setFormData({
      desc_regra: '',
      idade_inicial_regra: '',
      idade_final_regra: '',
      taxa: '',
      ativo_taxa: '',
      dt_desativa_regra: '',
      obs_taxa: '',
      reajuste_regra: ''
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.desc_regra.trim()) {
      toast.error('Descrição é obrigatória');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        dt_desativa_regra: formData.ativo_taxa === '0' ? formatDateToISO(formData.dt_desativa_regra) : null
      };

      if (editingRegra) {
        await api.put(`/regras/${editingRegra.id_regra}`, payload);
        toast.success('Regra atualizada com sucesso!');
      } else {
        await api.post('/regras', payload);
        toast.success('Regra criada com sucesso!');
      }
      closeModal();
      loadRegras();
    } catch (error) {
      console.error('Erro ao salvar regra:', error);
      toast.error('Erro ao salvar regra');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Regras</h1>
        <p className="text-gray-600">Gerencie regras e taxas</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Layers className="h-8 w-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total de Regras</p>
              <p className="text-2xl font-bold text-gray-900">{regras.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Search className="h-8 w-8 text-green-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Filtradas</p>
              <p className="text-2xl font-bold text-gray-900">{filteredRegras.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Plus className="h-8 w-8 text-purple-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Ações</p>
              <IconButton icon={Plus} onClick={() => openModal()} variant="primary" title="Nova Regra" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Buscar regras..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <IconButton icon={Plus} onClick={() => openModal()} variant="primary" title="Nova Regra" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descrição</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Idade Inicial</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Idade Final</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Taxa</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ativo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Info</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRegras.map((regra) => (
                <tr key={regra.id_regra} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">{regra.id_regra}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{regra.desc_regra || '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{regra.idade_inicial_regra ?? '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{regra.idade_final_regra ?? '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{regra.taxa || '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {regra.ativo_taxa === 1 ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        ATIVO
                      </span>
                    ) : regra.ativo_taxa === 0 ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        INATIVO
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <IconButton
                        icon={MessageSquare}
                        onClick={() => setObsModalItem(regra)}
                        variant="info"
                        size="sm"
                        title={regra.obs_taxa != null && String(regra.obs_taxa).trim() ? 'Ver observação' : 'Sem observação'}
                        disabled={!(regra.obs_taxa != null && String(regra.obs_taxa).trim())}
                      />
                      <IconButton
                        icon={FileText}
                        onClick={() => setReajusteModalItem(regra)}
                        variant="info"
                        size="sm"
                        title={regra.reajuste_regra != null && String(regra.reajuste_regra).trim() ? 'Ver reajuste' : 'Sem reajuste'}
                        disabled={!(regra.reajuste_regra != null && String(regra.reajuste_regra).trim())}
                      />
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <IconButton icon={Edit3} onClick={() => openModal(regra)} variant="warning" size="sm" title="Editar" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredRegras.length === 0 && (
          <div className="text-center py-12">
            <Layers className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma regra encontrada</h3>
            <p className="mt-1 text-sm text-gray-500">{searchTerm ? 'Tente ajustar sua busca.' : 'Comece criando uma nova regra.'}</p>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{editingRegra ? 'Editar Regra' : 'Nova Regra'}</h3>
              <form onSubmit={handleSave} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrição *</label>
                  <input type="text" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={formData.desc_regra} onChange={(e) => setFormData({ ...formData, desc_regra: e.target.value })} />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Idade Inicial</label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      value={formData.idade_inicial_regra}
                      onChange={(e) => setFormData({ ...formData, idade_inicial_regra: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Idade Final</label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      value={formData.idade_final_regra}
                      onChange={(e) => setFormData({ ...formData, idade_final_regra: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Taxa</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={formData.taxa}
                    onChange={(e) => setFormData({ ...formData, taxa: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ativo</label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      value={formData.ativo_taxa}
                      onChange={(e) => {
                        const newVal = e.target.value;
                        setFormData({ 
                            ...formData, 
                            ativo_taxa: newVal,
                            dt_desativa_regra: newVal === '1' ? '' : formData.dt_desativa_regra
                        });
                      }}
                    >
                      <option value="1">ATIVO</option>
                      <option value="0">INATIVO</option>
                    </select>
                  </div>
                  
                  {formData.ativo_taxa === '0' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Data Desativação</label>
                      <div className="relative">
                        <input
                            type="text"
                            className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            value={formData.dt_desativa_regra}
                            onChange={(e) => setFormData({ ...formData, dt_desativa_regra: formatDateInput(e.target.value) })}
                            placeholder="dd/mm/aaaa"
                        />
                         <button
                            type="button"
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-blue-500"
                            onClick={() => {
                                if (dtDesativaRef.current?.showPicker) dtDesativaRef.current.showPicker();
                                else dtDesativaRef.current?.focus();
                            }}
                        >
                            <Calendar className="h-5 w-5" />
                        </button>
                        <input
                            type="date"
                            ref={dtDesativaRef}
                            className="absolute opacity-0 pointer-events-none bottom-0 left-0"
                            onChange={(e) => setFormData({ ...formData, dt_desativa_regra: isoToDisplay(e.target.value) })}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reajuste</label>
                  <input
                    type="text"
                    maxLength={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={formData.reajuste_regra}
                    onChange={(e) => setFormData({ ...formData, reajuste_regra: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                    value={formData.obs_taxa}
                    onChange={(e) => setFormData({ ...formData, obs_taxa: e.target.value })}
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <IconButton icon={X} onClick={closeModal} variant="secondary" title="Cancelar" />
                  <IconButton icon={Save} type="submit" disabled={isSaving} variant="primary" title={isSaving ? 'Salvando...' : (editingRegra ? 'Atualizar' : 'Criar')} />
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Observação */}
      {obsModalItem && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" onClick={() => setObsModalItem(null)}>
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Observação da Regra #{obsModalItem.id_regra}</h3>
              <div className="p-3 border border-gray-200 rounded bg-gray-50 whitespace-pre-wrap text-sm text-gray-800">
                {obsModalItem.obs_taxa != null && String(obsModalItem.obs_taxa).trim() ? String(obsModalItem.obs_taxa) : 'Sem observação.'}
              </div>
              <div className="mt-4 flex justify-end">
                <IconButton icon={X} onClick={() => setObsModalItem(null)} variant="secondary" title="Fechar" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Reajuste */}
      {reajusteModalItem && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" onClick={() => setReajusteModalItem(null)}>
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Reajuste da Regra #{reajusteModalItem.id_regra}</h3>
              <div className="p-3 border border-gray-200 rounded bg-gray-50 whitespace-pre-wrap text-sm text-gray-800">
                {reajusteModalItem.reajuste_regra != null && String(reajusteModalItem.reajuste_regra).trim() ? String(reajusteModalItem.reajuste_regra) : 'Sem informações de reajuste.'}
              </div>
              <div className="mt-4 flex justify-end">
                <IconButton icon={X} onClick={() => setReajusteModalItem(null)} variant="secondary" title="Fechar" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Regras;
